import { ArrowDownUp, Goal, Minus, Plus, ShieldAlert, Star, UserRound, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ClubLogo } from './ClubLogo';
import { useI18n } from './I18nContext';
import type { LivePlayerPerformance, Player } from './types';

export function LivePlayerPerformancePanel({
  players, matchId, teamId, minute, performance, setPerformance, onSubstitution,
}: {
  players: Player[];
  matchId: string;
  teamId: string;
  minute: number;
  performance: Record<string, LivePlayerPerformance>;
  setPerformance: React.Dispatch<React.SetStateAction<Record<string, LivePlayerPerformance>>>;
  onSubstitution: (playerOut: string, playerIn: string) => void;
}) {
  const { t } = useI18n();
  const [selected, setSelected] = useState('');
  const [subOut, setSubOut] = useState('');
  const [subIn, setSubIn] = useState('');

  const ensure = (player: Player): LivePlayerPerformance => performance[player.id] || {
    match_id: matchId, team_id: teamId, player_id: player.id,
    started: false, on_pitch: false, minute_on: 0, minute_off: null,
    rating: Number(player.current_form ?? 6), goals: 0, assists: 0,
    yellow_cards: 0, red_cards: 0, shots: 0, shots_on_target: 0, key_passes: 0,
    touches: 0, duels_won: 0, duels_lost: 0, tackles_won: 0, tackles_lost: 0,
    interceptions: 0, clearances: 0, headers_won: 0, headers_lost: 0,
    fouls_won: 0, fouls_committed: 0, offsides: 0, saves: 0, ball_losses: 0, notes: '',
  };

  const sorted = useMemo(() => [...players].sort((a,b) => {
    const pa = ensure(a), pb = ensure(b);
    if (pb.on_pitch !== pa.on_pitch) return Number(pb.on_pitch) - Number(pa.on_pitch);
    if (pb.started !== pa.started) return Number(pb.started) - Number(pa.started);
    return a.name.localeCompare(b.name);
  }), [players, performance]);

  const patch = (player: Player, update: Partial<LivePlayerPerformance>) => {
    setPerformance(current => ({
      ...current,
      [player.id]: { ...ensure(player), ...current[player.id], ...update },
    }));
  };

  const selectedPlayer = players.find(p => p.id === selected);
  const selectedPerf = selectedPlayer ? ensure(selectedPlayer) : null;

  return (
    <section className="metal-card live-player-performance-panel">
      <div className="section-title-line">
        <div><p className="eyebrow">{t.playerPerformance}</p><h2>{t.liveRatings}</h2></div>
        <span>{sorted.filter(p => ensure(p).on_pitch).length} {t.onPitch}</span>
      </div>

      <div className="live-player-performance-grid">
        {sorted.map(player => {
          const perf = ensure(player);
          return (
            <button key={player.id} className={`performance-player-card ${perf.on_pitch?'on-pitch':''} ${perf.started?'starter':''}`} onClick={() => setSelected(player.id)}>
              <span className="performance-avatar">
                {player.photo_url ? <img src={player.photo_url} alt="" /> : <ClubLogo club="Polonia Glasgow" className="performance-placeholder-logo" />}
                <i>{player.squad_number ?? '—'}</i>
              </span>
              <span className="performance-copy"><strong>{player.name}</strong><small>{player.position || '—'} · {perf.on_pitch ? t.onPitch : t.bench}</small></span>
              <span className="performance-rating">{perf.rating.toFixed(1)}</span>
            </button>
          );
        })}
      </div>

      <div className="substitution-control">
        <select value={subOut} onChange={e=>setSubOut(e.target.value)}>
          <option value="">{t.playerOut}</option>
          {players.filter(p=>ensure(p).on_pitch).map(p=><option key={p.id} value={p.id}>#{p.squad_number ?? '—'} {p.name}</option>)}
        </select>
        <span>→</span>
        <select value={subIn} onChange={e=>setSubIn(e.target.value)}>
          <option value="">{t.playerIn}</option>
          {players.filter(p=>!ensure(p).on_pitch).map(p=><option key={p.id} value={p.id}>#{p.squad_number ?? '—'} {p.name}</option>)}
        </select>
        <button className="gold-button" onClick={()=>{
          if(!subOut || !subIn) return;
          const po=players.find(p=>p.id===subOut), pi=players.find(p=>p.id===subIn);
          if(po) patch(po,{on_pitch:false,minute_off:minute});
          if(pi) patch(pi,{on_pitch:true,minute_on:minute,minute_off:null});
          onSubstitution(subOut,subIn); setSubOut(''); setSubIn('');
        }}><ArrowDownUp size={16}/>{t.confirmSubstitution}</button>
      </div>

      {selectedPlayer && selectedPerf && (
        <div className="performance-modal-backdrop">
          <section className="metal-card performance-modal">
            <button className="modal-close" onClick={()=>setSelected('')}><X/></button>
            <div className="performance-modal-hero">
              <span className="performance-modal-avatar">{selectedPlayer.photo_url ? <img src={selectedPlayer.photo_url} alt="" /> : <UserRound/>}</span>
              <div><p className="eyebrow">{t.livePlayerCard}</p><h2>{selectedPlayer.name}</h2><small>#{selectedPlayer.squad_number ?? '—'} · {selectedPlayer.position || '—'}</small></div>
              <strong>{selectedPerf.rating.toFixed(1)}</strong>
            </div>

            <div className="starter-toggle-row">
              <button className={selectedPerf.started?'active':''} onClick={()=>patch(selectedPlayer,{started:true,on_pitch:true,minute_on:0,minute_off:null})}>{t.startingXI}</button>
              <button className={!selectedPerf.started?'active':''} onClick={()=>patch(selectedPlayer,{started:false,on_pitch:false,minute_on:0,minute_off:null})}>{t.bench}</button>
            </div>

            <div className="rating-adjuster">
              <button onClick={()=>patch(selectedPlayer,{rating:Math.max(1,selectedPerf.rating-.1)})}><Minus/></button>
              <strong><Star size={20}/>{selectedPerf.rating.toFixed(1)}</strong>
              <button onClick={()=>patch(selectedPlayer,{rating:Math.min(10,selectedPerf.rating+.1)})}><Plus/></button>
            </div>

            <div className="performance-stat-editor">
              {([
                ['goals',t.goals,Goal],['assists',t.assists,Goal],['yellow_cards',t.yellowCard,ShieldAlert],['red_cards',t.redCard,ShieldAlert]
              ] as const).map(([key,label,Icon])=>(
                <div key={key}><span><Icon size={17}/>{label}</span><div>
                  <button onClick={()=>patch(selectedPlayer,{[key]:Math.max(0,Number(selectedPerf[key])-1)})}>−</button>
                  <strong>{selectedPerf[key]}</strong>
                  <button onClick={()=>patch(selectedPlayer,{[key]:Number(selectedPerf[key])+1})}>+</button>
                </div></div>
              ))}
            </div>

            <label className="performance-notes"><span>{t.coachNotes}</span><textarea value={selectedPerf.notes} onChange={e=>patch(selectedPlayer,{notes:e.target.value})} placeholder={t.playerNotesPlaceholder}/></label>
          </section>
        </div>
      )}
    </section>
  );
}
